import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import { BRAND_NAME } from '@/config/constants';
import { useAuthStore } from '@/store/auth.store';
import { useAgentSelectedBotStore } from '@/store/agentSelectedBot.store';
import { getBotIdentifierFromLocationForHeader } from '@/utils/sidebarContext';
import * as botService from '@/services/api/bot.service';
import ThemeSwitcher from './ThemeSwitcher';
import NotificationBell from './NotificationBell';
import './Header.css';

const { Header: AntHeader } = Layout;

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/bots': 'Bots',
  '/tenants': 'Tenants',
  '/users': 'Users',
  '/platform/users': 'Platform users',
  '/tickets': 'Tickets',
  '/knowledge-base': 'Knowledge base',
  '/analytics': 'Platform analytics',
  '/monitoring': 'Monitoring',
  '/agent-analytics': 'Agent analytics',
  '/reporting': 'Reporting',
  '/logs': 'Log viewer',
  '/livechat': 'Chat log',
  '/channels': 'Channels',
  '/actions': 'Custom actions',
  '/integrations/google': 'Google connections',
  '/config': 'Platform configuration',
  '/announcements': 'Announcements',
  '/internal-chat-logs': 'Internal chat logs',
  '/profile': 'Profile',
  '/developer-docs': 'Developer docs',
  '/dashboard/ai-settings': 'AI Settings',
};

const Header = () => {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const workspaceBot = useAgentSelectedBotStore((s) => s.selection);

  const pageTitle = useMemo(() => {
    if (location.pathname === '/dashboard') {
      const r = user?.role?.toLowerCase();
      if (r === 'superadmin') return 'Dashboard';
      return 'Home';
    }
    if (location.pathname === '/reporting' && location.search.includes('botId=')) {
      return 'Bot Reporting';
    }
    if (PAGE_TITLES[location.pathname]) {
      return PAGE_TITLES[location.pathname];
    }
    if (/^\/tenants\/[^/]+\/?$/.test(location.pathname)) {
      return 'Home';
    }
    if (/^\/tenants\/[^/]+\/users\/?$/.test(location.pathname)) {
      return 'Users';
    }
    if (/^\/tenants\/[^/]+\/bots\/?$/.test(location.pathname)) {
      return 'Bots';
    }
    if (/^\/analytics\/tenant\/[^/]+\/?$/.test(location.pathname)) {
      return 'Analytics';
    }
    if (/^\/analytics\/bot\/?$/.test(location.pathname)) {
      return 'Bot Analytics';
    }
    if (/^\/skills\/?$/.test(location.pathname)) {
      return 'Skills';
    }
    if (/^\/help\/agent\/?$/.test(location.pathname)) {
      return 'Agent guide';
    }
    if (/^\/help\/?$/.test(location.pathname)) {
      return 'Help';
    }
    if (/^\/admin\/ai-chats\/?$/.test(location.pathname)) {
      return 'AI chats';
    }
    if (/^\/admin\/ai-learning\/?$/.test(location.pathname)) {
      return 'AI learning';
    }
    if (/^\/dashboard\/workspace-settings\/?$/.test(location.pathname)) {
      return 'Live Chat Settings';
    }
    if (/^\/dashboard\/ai-settings\/?$/.test(location.pathname)) {
      return 'AI Settings';
    }
    if (/^\/tenants\/[^/]+\/workspace-settings\/?$/.test(location.pathname)) {
      return 'Live Chat Settings';
    }
    if (/^\/tenants\/[^/]+\/ai-settings\/?$/.test(location.pathname)) {
      return 'AI Settings';
    }
    if (/^\/tenants\/[^/]+\/reporting\/?$/.test(location.pathname)) {
      return 'Reporting';
    }
    if (/^\/tenants\/[^/]+\/agent-analytics\/?$/.test(location.pathname)) {
      return 'Agent analytics';
    }
    if (/^\/tenants\/[^/]+\/tickets\/?$/.test(location.pathname)) {
      return 'Tickets';
    }
    if (/^\/tenants\/[^/]+\/tickets\/[^/]+\/?$/.test(location.pathname)) {
      return 'Ticket';
    }
    if (/^\/admin\/workspace-settings\/?$/.test(location.pathname)) {
      return 'Live Chat Settings';
    }
    if (/^\/livechat\/workspace\/?$/.test(location.pathname)) {
      return 'Live Chat';
    }
    if (/^\/tickets\/[^/]+\/?$/.test(location.pathname)) {
      return 'Ticket';
    }
    if (/^\/bots\/[^/]+\/overview\/?$/.test(location.pathname)) {
      return 'Bot overview';
    }
    const basePath = '/' + location.pathname.split('/')[1];
    return PAGE_TITLES[basePath] || BRAND_NAME;
  }, [location.pathname, location.search, user?.role]);

  const headerScopedBotId = useMemo(
    () => getBotIdentifierFromLocationForHeader(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const [scopedBotDisplayName, setScopedBotDisplayName] = useState<string | null>(null);

  useEffect(() => {
    if (!headerScopedBotId) {
      setScopedBotDisplayName(null);
      return;
    }
    let cancelled = false;
    setScopedBotDisplayName(null);
    void botService
      .getBot(headerScopedBotId)
      .then((b) => {
        if (cancelled) return;
        const n = b?.name?.trim();
        setScopedBotDisplayName(n && n.length > 0 ? n : null);
      })
      .catch(() => {
        if (!cancelled) setScopedBotDisplayName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [headerScopedBotId]);

  const agentWorkspaceSubtitle = useMemo(() => {
    if (user?.role?.toLowerCase() !== 'agent') return null;
    const name = workspaceBot?.name?.trim() || null;
    const slug = workspaceBot?.bot_id?.trim() || null;
    const line = name || slug;
    if (!line) return null;
    return { line };
  }, [user?.role, workspaceBot?.name, workspaceBot?.bot_id]);

  const suppressDuplicateScopedBotName =
    user?.role?.toLowerCase() === 'agent' &&
    !!scopedBotDisplayName &&
    !!agentWorkspaceSubtitle &&
    agentWorkspaceSubtitle.line === scopedBotDisplayName;

  const showHeaderScopedBotName =
    !!headerScopedBotId && !!scopedBotDisplayName && !suppressDuplicateScopedBotName;

  return (
    <AntHeader className="header">
      <div className="header-content">
        <div className="header-left">
          <div className="header-title-block">
            <h2 className="page-title">{pageTitle}</h2>
            {showHeaderScopedBotName ? (
              <div className="page-title-bot-name-row" aria-label="Bot">
                <span className="page-title-bot-name-label">Bot</span>
                <span className="page-title-bot-name-value">{scopedBotDisplayName}</span>
              </div>
            ) : null}
            {agentWorkspaceSubtitle ? (
              <div className="page-title-sub">
                <span className="page-title-sub-name">{agentWorkspaceSubtitle.line}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="header-right">
          <NotificationBell />
          <ThemeSwitcher />
        </div>
      </div>
    </AntHeader>
  );
};

export default Header;
